import React, { useState } from 'react'
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline'
import { Button, Card, DataTable, PageHeader, CreateModal, EditModal, FormField } from '@/shared/ui'
import type { FAQResponse, Column } from '@/shared/api'

interface FAQManagerProps {
  title: string
  description: string
  data: FAQResponse[]
  loading: boolean
  error: any
  includeInactive: boolean
  onToggleInactive: (value: boolean) => void
  onCreate: (title: string, description: string, sortOrder?: number) => void
  onUpdate: (id: number, title?: string, description?: string, isActive?: boolean, sortOrder?: number) => void
  onDelete: (id: number) => void
  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

interface CreateFormData {
  title: string
  description: string
  sortOrder: number
}

interface EditFormData {
  id: number
  title: string
  description: string
  isActive: boolean
  sortOrder: number
}

export const FAQManager: React.FC<FAQManagerProps> = ({
  title,
  description,
  data = [],
  loading = false,
  error,
  includeInactive = false,
  onToggleInactive,
  onCreate,
  onUpdate,
  onDelete,
  isCreating = false,
  isUpdating = false,
  isDeleting = false,
}) => {
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingItem, setEditingItem] = useState<EditFormData | null>(null)
  const [createForm, setCreateForm] = useState<CreateFormData>({
    title: '',
    description: '',
    sortOrder: 0,
  })

  const handleCreate = () => {
    if (!createForm.title.trim() || !createForm.description.trim()) return
    
    onCreate(
      createForm.title.trim(), 
      createForm.description.trim(),
      createForm.sortOrder
    )
    
    // Сброс формы
    setCreateForm({ title: '', description: '', sortOrder: 0 })
    setShowCreateForm(false)
  }

  const handleEdit = (item: FAQResponse) => {
    if (!item?.faq_id) return
    
    setEditingItem({
      id: item.faq_id,
      title: item.title || '',
      description: item.description || '',
      isActive: Boolean(item.is_active),
      sortOrder: item.sort_order ?? 0,
    })
  }

  const handleUpdate = () => {
    if (!editingItem || !editingItem.title.trim() || !editingItem.description.trim()) return
    
    onUpdate(
      editingItem.id,
      editingItem.title.trim(),
      editingItem.description.trim(),
      editingItem.isActive,
      editingItem.sortOrder
    )
    
    setEditingItem(null)
  }

  const handleDelete = (id: number, title: string) => {
    if (confirm(`Вы уверены, что хотите удалить FAQ "${title}"?`)) {
      onDelete(id)
    }
  }

  // Колонки таблицы
  const columns: Column<FAQResponse>[] = [
    {
      key: 'title' as const,
      title: 'Вопрос',
      render: (_, item) => (
        <div className={`font-medium ${!item?.is_active ? 'text-gray-400' : 'text-gray-900'}`}>
          <div className="max-w-xs truncate" title={item?.title}>
            {item?.title || '—'}
          </div>
        </div>
      ),
    },
    {
      key: 'description' as const,
      title: 'Ответ',
      render: (_, item) => (
        <div className={`text-sm ${!item?.is_active ? 'text-gray-400' : 'text-gray-600'}`}>
          <div className="max-w-md truncate" title={item?.description}>
            {item?.description || '—'}
          </div>
        </div>
      ),
    },
    {
      key: 'sort_order' as const,
      title: 'Порядок',
      width: '20',
      render: (_, item) => (
        <span className="text-sm text-gray-500">
          {item?.sort_order ?? 0}
        </span>
      ),
    },
    {
      key: 'is_active' as const,
      title: 'Статус',
      width: '20',
      render: (_, item) => (
        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
          Boolean(item?.is_active)
            ? 'bg-green-100 text-green-800' 
            : 'bg-red-100 text-red-800'
        }`}>
          {Boolean(item?.is_active) ? 'Активен' : 'Неактивен'}
        </span>
      ),
    },
    {
      key: 'created_at_formatted' as const,
      title: 'Создан',
      width: '24',
      render: (_, item) => (
        <span className="text-sm text-gray-500">
          {item?.created_at_formatted || '—'}
        </span>
      ),
    },
    {
      key: 'actions' as const,
      title: 'Действия',
      width: '32',
      render: (_, item) => {
        if (!item?.faq_id) return null
        
        return (
          <div className="flex space-x-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation()
                handleEdit(item)
              }}
              disabled={isUpdating}
            >
              <PencilIcon className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation()
                handleDelete(item.faq_id, item.title || 'FAQ')
              }}
              disabled={isDeleting}
            >
              <TrashIcon className="h-4 w-4 text-red-500" />
            </Button>
          </div>
        )
      },
    },
  ]

  if (error) {
    return (
      <>
        <PageHeader title={title} description={description} />
        <div className="flex-1 p-6">
          <Card>
            <div className="text-center py-12">
              <h3 className="text-lg font-medium text-red-600 mb-2">
                Ошибка загрузки данных
              </h3>
              <p className="text-gray-500">{error.detail || 'Произошла ошибка'}</p>
            </div>
          </Card>
        </div>
      </>
    )
  }

  return (
    <>
      <PageHeader title={title} description={description}>
        <Button
          onClick={() => setShowCreateForm(true)}
          disabled={isCreating}
        >
          {isCreating ? 'Создание...' : 'Создать'}
        </Button>
      </PageHeader>

      <div className="flex-1 p-6 overflow-auto">
        {/* Модальное окно создания */}
        <CreateModal
          isOpen={showCreateForm}
          onClose={() => setShowCreateForm(false)}
          title="Создать новый FAQ"
          fields={[
            {
              name: 'title',
              label: 'Вопрос',
              type: 'text',
              required: true,
              placeholder: 'Введите вопрос',
              value: createForm.title,
              onChange: (value) => setCreateForm(prev => ({ ...prev, title: value }))
            },
            {
              name: 'description',
              label: 'Ответ',
              type: 'textarea',
              required: true,
              placeholder: 'Введите ответ',
              value: createForm.description,
              onChange: (value) => setCreateForm(prev => ({ ...prev, description: value }))
            },
            {
              name: 'sortOrder',
              label: 'Порядок сортировки',
              type: 'number',
              value: createForm.sortOrder,
              onChange: (value) => setCreateForm(prev => ({ ...prev, sortOrder: value }))
            }
          ]}
          onSubmit={handleCreate}
          onCancel={() => {
            setCreateForm({ title: '', description: '', sortOrder: 0 })
          }}
          isLoading={isCreating}
        />

        {/* Модальное окно редактирования */}
        <EditModal
          isOpen={!!editingItem}
          onClose={() => setEditingItem(null)}
          title="Редактировать FAQ"
          fields={[
            {
              name: 'title',
              label: 'Вопрос',
              type: 'text',
              required: true,
              placeholder: 'Введите вопрос',
              value: editingItem?.title || '',
              onChange: (value) => setEditingItem(prev => prev ? ({ ...prev, title: value }) : null)
            },
            {
              name: 'description',
              label: 'Ответ',
              type: 'textarea',
              required: true,
              placeholder: 'Введите ответ',
              value: editingItem?.description || '',
              onChange: (value) => setEditingItem(prev => prev ? ({ ...prev, description: value }) : null)
            },
            {
              name: 'sortOrder',
              label: 'Порядок сортировки',
              type: 'number',
              value: editingItem?.sortOrder || 0,
              onChange: (value) => setEditingItem(prev => prev ? ({ ...prev, sortOrder: value }) : null)
            },
            {
              name: 'isActive',
              label: 'Активен',
              type: 'checkbox',
              value: editingItem?.isActive || false,
              onChange: (value) => setEditingItem(prev => prev ? ({ ...prev, isActive: value }) : null)
            }
          ]}
          onSubmit={handleUpdate}
          onCancel={() => setEditingItem(null)}
          isLoading={isUpdating}
        />

        {/* Таблица */}
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          emptyMessage="FAQ не найдены"
        />
      </div>
    </>
  )
}
